// The one nav for auditfriendly.co and jobs.auditfriendly.co.
//
// It renders the SAME source files the Webflow site builds its nav embed from
// (site/webflow/home-v4/src/nav.{html,css,js}), so the two sites cannot drift:
// edit the nav there, rebuild the Webflow embed, and the app picks up the same
// change on its next deploy.
//
// Mounted once above <Routes> and never unmounted (nav.js wires document-level
// listeners once). Routes with their own chrome hide it instead. Links into
// jobs.auditfriendly.co are routed in-app rather than reloading the page.
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { siteEvents } from "@/lib/siteEvents";
import { trackOfferClick } from "@/lib/offerTracking";
import navHtml from "../../site/webflow/home-v4/src/nav.html?raw";
import navCss from "../../site/webflow/home-v4/src/nav.css?raw";
import navJs from "../../site/webflow/home-v4/src/nav.js?raw";

// The anon key nav.js uses for the public home_live RPC (the same public key
// the app's own Supabase client ships with).
const ANON_KEY =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6IndkdGZudHR0cXJua3phY2VkemxpIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDU4MTMwNDQsImV4cCI6MjA2MTM4OTA0NH0.g5WaSpJHpYG33xtFtHpcsNSkdI2ntMA79rmo4UlUslg";

// Webflow bakes these at build time; here nav.js fills them from home_live a
// moment after load (every one has a data-live binding), so they start empty.
const MARKUP = navHtml.replace(/\{\{[\w.]+\}\}/g, (m) => (m === "{{strip.announcement.url}}" ? "https://auditfriendly.co/software" : ""));
const SCRIPT = navJs.replace("{{SUPABASE_ANON_KEY}}", ANON_KEY);

// Routes that bring their own chrome (the signed-in portal, admin, embeds).
const HIDDEN = [/^\/dashboard(\/|$)/, /^\/admin(\/|$)/, /^\/internal(\/|$)/, /^\/embed(\/|$)/, /^\/recruiter(\/|$)/, /^\/beta(\/|$)/, /^\/go(\/|$)/];

const APP_HOSTS = new Set(["jobs.auditfriendly.co"]);

export default function GlobalNav() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  const [signedIn, setSignedIn] = useState(false);
  const hidden = HIDDEN.some((re) => re.test(pathname));

  // CSS once, then nav.js once, after the markup is in the document.
  useEffect(() => {
    if (!document.getElementById("afn-css")) {
      const style = document.createElement("style");
      style.id = "afn-css";
      style.textContent = navCss;
      document.head.appendChild(style);
    }
    if (!(window as unknown as { __afnBooted?: boolean }).__afnBooted) {
      (window as unknown as { __afnBooted?: boolean }).__afnBooted = true;
      // eslint-disable-next-line no-new-func
      new Function(SCRIPT)();
    }
  }, []);

  // Links into the job board stay inside the app.
  useEffect(() => {
    let libraryViewed = false;
    const onClick = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      // Offer funnel: opening the Library menu counts as a view of its offers,
      // and a link marked data-offer counts as a click, surface "nav".
      if (!libraryViewed && t?.closest?.('.afn [aria-controls="afn-p-library"]')) {
        libraryViewed = true;
        siteEvents.act("offer_view", { key: "library", surface: "nav" });
      }
      const offerKey = (t?.closest?.(".afn a[data-offer], .afn-drawer a[data-offer]") as HTMLAnchorElement | null)?.dataset.offer;
      if (offerKey) trackOfferClick(offerKey, "nav");
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = t?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!a || !a.closest(".afn, .afn-sov, .afn-drawer") || a.target === "_blank") return;
      let url: URL;
      try { url = new URL(a.href); } catch { return; }
      if (!APP_HOSTS.has(url.host) && url.host !== window.location.host) return;
      e.preventDefault();
      // Close whatever is open (menus, drawer, search) the way nav.js does.
      document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape" }));
      document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape" }));
      navigate(url.pathname + url.search + url.hash);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [navigate]);

  // "Sign in" becomes "My dashboard" for a signed-in member.
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSignedIn(!!data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSignedIn(!!s));
    return () => sub.subscription.unsubscribe();
  }, []);
  useEffect(() => {
    document.querySelectorAll<HTMLAnchorElement>('.afn a[href$="/sign-in"], .afn a[href$="/dashboard"][data-afn-account]').forEach((a) => {
      a.setAttribute("data-afn-account", "");
      a.href = signedIn ? "https://jobs.auditfriendly.co/dashboard" : "https://jobs.auditfriendly.co/sign-in";
      a.textContent = signedIn ? "My dashboard" : "Sign in";
    });
  }, [signedIn]);

  return (
    <div ref={ref} className="af-global-nav" hidden={hidden}>
      <div dangerouslySetInnerHTML={{ __html: MARKUP }} />
      {/* The nav is position:fixed at 96px; this keeps page content below it. */}
      <div className="navbar-holder" aria-hidden="true" style={{ height: 96 }} />
    </div>
  );
}
